import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { colors, css, glyphs } from "../../../Styles";
import { MainContentPaddingTypes, NewButton, NewModal, SuccessAnimation, WaitAnimation, SettingsContent, EsfSectionModal, EsfStationModal } from "../..";
import { SettingsContentHandles } from "../SubComponents/settingsContent";
import { currentContentTypes, currentInnerContentTypes, currentSubContentTypes, ModalContext, useModalContext } from "../../GlobalModals/_GlobalModals/globalModalContext";
import { System } from "../../../Services";
import { LocationWithSectionAndSystem, selectLocationsBySystem, useLocationById } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";
import { selectStationByLocationId, useStationById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { RootState, useAppSelector } from "../../../Store";
import { isEqual } from "lodash";
import { selectStationStatusById, useStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
// import { useEsfStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";

interface Esf_ModalProps {
  system: System;
}

export default function Esf_Modal({ system }: Esf_ModalProps) {
  const { t } = useTranslation();
  const { state, dispatch } = useModalContext();
  const settingsRef = useRef<SettingsContentHandles>(null);
  const successMode = useSelector((state: RootState) => state.uiState.successMode.successMode);
  
  useEffect(() => {
    if (!state.message) return;
    const timer = setTimeout(() => {
      dispatch({ message: undefined });
    }, 2000);
    return () => clearTimeout(timer);
  }, [state.message]);

  // #region | Location Info
  const { isLoading: loadingLocation, entity: location } = useLocationById(state.activeLocation);
  
  const locations = useAppSelector((state) => selectLocationsBySystem(state, [system.system_id]), isEqual);
  
  const currentLocationIndex = locations.findIndex(s => s.location_id === state.activeLocation);
  // #endregion

  // #region | Station Info
  const { isLoading: loadingStation, entity: station } = useStationById(state.activeStation);

  const stations = useAppSelector((state) => selectStationByLocationId(state, location?.location_id ?? -1), isEqual);

  const currentStationIndex = stations.findIndex(s => s.station_id === state.activeStation);
  // #endregion
  
  // #region | Modal Content Router
  const modalContentRouter = () => {
    if (!system || !state.connection || !state.domain || !state.farm ) return;

    let header = "";
    let subHeader = "";
    let mainContent = <></>;
    let goBack: undefined | (() => void) = () => { };
    let next: undefined | (() => void) = () => { };
    let prev: undefined | (() => void) = () => { };
    
    switch (state.currentContent) {
      case currentContentTypes.station:
        switch (state.currentSubContent) {
          case currentSubContentTypes.settings:
            header = `${t("", { defaultValue: "Station" })} ${station?.name}`
            subHeader = `${system.name} ${location?.name}`
            mainContent = <>
              {loadingStation && <WaitAnimation />}
              {!loadingStation && !station && <p style={{...css.emptyPen}}>No station found</p>}
              {!loadingStation && station && <SettingsContent ref={settingsRef} station={station}/>}
            </>
            next = undefined
            prev = undefined
            goBack = undefined
          break;
          case currentSubContentTypes.overview:
          default:
            header = `${t("", { defaultValue: "Station" })} ${station?.name}`
            subHeader = `${system.name} ${location?.name}`
            mainContent = <>
              {loadingStation && <WaitAnimation />}
              {!loadingStation && !station && <p style={{...css.emptyPen}}>No station found</p>}
              {!loadingStation && state.currentContent === currentContentTypes.station && state.activeStation && <EsfStationModal activeStation={state.activeStation} />}
            </>
            next = () => dispatch({ activeStation: stations[(currentStationIndex + 1) % stations.length].station_id })
            prev = () => dispatch({ activeStation: stations[(currentStationIndex - 1 + stations.length) % stations.length].station_id })
            goBack = () => { dispatch({
              currentContent: currentContentTypes.location,
              currentSubContent: currentSubContentTypes.overview,
              currentInnerContent: currentInnerContentTypes.stations
            })}
          break;
        }
        break;
      case currentContentTypes.location:
      default:
        header = loadingLocation ? "" : `${location?.name}`
        subHeader = loadingLocation ? "" : `${system.name}`
        mainContent = <>
          {loadingLocation && <WaitAnimation />}
          {!loadingLocation && !location && <p style={{...css.emptyPen}}>No location found</p>}
          {!loadingLocation && location && <EsfSectionModal
            system={system}
            pen={location as LocationWithSectionAndSystem}
            stations={location?.stations ?? []}
          />}
        </>
        next = () => dispatch({ activeLocation: locations[(currentLocationIndex + 1) % locations.length].location_id })
        prev = () => dispatch({activeLocation: locations[(currentLocationIndex - 1 + locations.length) % locations.length].location_id})
        goBack = undefined
    }
    return { goBack, header, subHeader, mainContent, next, prev }
  }
  // #endregion

  return (
    <ModalContext.Provider value={{ state, dispatch }}>
      <NewModal
        header={modalContentRouter()?.header ?? ""}
        subHeader={modalContentRouter()?.subHeader}
        overrides={{
          backgroundColor: state.currentSubContent === currentSubContentTypes.settings ? colors.blueDarkPrimary : colors.greyLightTertiary,
          mainContentPadding: MainContentPaddingTypes.primary,
          secondaryContentScroll: true,
          mainContentScroll: true,
        }}
        predefinedButtons={{
          onClose: () => {
            dispatch({
              activeLocation: undefined,
              activeStation: undefined,
              currentContent: undefined,
              currentSubContent: undefined,
              currentInnerContent: undefined
            })
          },
          onGoBack: modalContentRouter()?.goBack,
          onPrev: modalContentRouter()?.prev,
          onNext: modalContentRouter()?.next
        }}
        tabButtons={{
          contents: state.currentContent === currentContentTypes.station ? ["overview", "config"] : [],
          disabled: state.currentSubContent === currentSubContentTypes.settings ? 1 : 0,
          active: state.currentSubContent === currentSubContentTypes.settings ? 1 : 0,
          startEnhancers: [glyphs.Animal({}), glyphs.Setting({})],
          onClick: (i) => {
            if (i === 0) { dispatch({currentSubContent: currentSubContentTypes.overview}) }
            if (i === 1) { dispatch({currentSubContent: currentSubContentTypes.settings}) }
          }
        }}
        mainContent={ state.currentSubContent !== currentSubContentTypes.settings ?
          <div style={{ width: "fit-content", display: "flex", flexDirection: "column", alignItems: "center" }}>
            {modalContentRouter()?.mainContent}
          </div>
        : undefined }
        secondaryContent={ state.currentSubContent === currentSubContentTypes.settings ?
          <div style={{ width: "fit-content", display: "flex", flexDirection: "column", alignItems: "center" }}>
            {modalContentRouter()?.mainContent}
          </div>
        : undefined }
        footerContent={state.currentSubContent === currentSubContentTypes.settings ?
          <NewButton content={t("buttons.save", {defaultValue: "save"})} onClick={() => settingsRef.current?.save()}/>
          : undefined}
        footerMessage={state.currentSubContent === currentSubContentTypes.settings ?
          state.message ?? ""
        : undefined }
      />

      { successMode && <SuccessAnimation/> }
    </ModalContext.Provider>
  )
}

